import { Data, UniqueIdentifier, useDraggable } from '@dnd-kit/core';
import { CSSProperties, Ref, cloneElement, isValidElement } from 'react';

export type DraggableData = Record<string, any>;

type DraggableChildProps = {
  ref?: Ref<HTMLElement>;
  style?: CSSProperties;
  'data-dragging'?: boolean;
  [key: string]: unknown;
};

export type TProps<T = DraggableData> = {
  id: UniqueIdentifier;
  data?: Data<T>;
  disabled?: boolean;
  children?: React.ReactElement<DraggableChildProps>;
  dragsSelf?: boolean;
};

const Draggable = <T,>(props: TProps<T>) => {
  const { id, data, disabled, children, dragsSelf = false } = props;

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useDraggable({
      id,
      data,
      disabled,
    });

  if (!children || !isValidElement(children)) {
    console.error(
      'Draggable component requires a single valid React element as a child.'
    );
    return children;
  }

  const DraggableChild = children;

  return cloneElement(DraggableChild, {
    ref: setNodeRef,
    'data-dragging': isDragging || undefined,
    ...listeners,
    ...attributes,
    style: dragsSelf
      ? {
          ...(DraggableChild.props.style || {}),
          transform: transform
            ? `translate(${transform.x}px, ${transform.y}px)`
            : undefined,
        }
      : undefined,
  });
};

export default Draggable;
